import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getReviewFunnel } from "../api";
import FunnelChart from "./review/FunnelChart";
import CoverageNotice from "./review/CoverageNotice";

const CATEGORIES = [
  { key: "personal", label: "Personal" },
  { key: "corporate", label: "Corporate" },
  { key: "tslips", label: "T-Slips" },
  { key: "other", label: "Other" },
];

// Mirrors the server's clients.views._is_manager, which the review endpoint
// gates on: a superuser with no employee role passes there, so it must pass
// here too, or they are told "managers only" about an API that would
// happily serve them.
const isManager = (user) =>
  Boolean(
    user &&
      (user.is_superuser ||
        ["MANAGER", "ADMIN"].includes(String(user.role).toUpperCase()))
  );

const today = () => new Date().toISOString().slice(0, 10);

// How far below the earliest year in play the picker still offers years.
// The list is derived from what came back rather than from a new endpoint;
// this run is what lets a manager reach BACK past the server's default
// cohorts, which are the three most recent years that have work.
const YEARS_BELOW = 3;

const chipStyle = (active) => ({
  padding: "4px 10px",
  background: active ? "#111827" : "#fff",
  color: active ? "#fff" : "#374151",
  border: "1px solid #D1D5DB",
  borderRadius: 999,
});

export default function ReviewPage({ user }) {
  const [asOf, setAsOf] = useState(today);
  const [categories, setCategories] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [groupBy, setGroupBy] = useState("category");
  const [funnel, setFunnel] = useState(null);
  const [error, setError] = useState("");

  const manager = isManager(user);

  // The server decides cohort membership for a given filter set -- the
  // funnel is reloaded whenever the as-of date, cut, or categories change
  // rather than filtered client-side.
  const load = useCallback(() => {
    if (!manager) return;
    const params = { as_of: asOf, group_by: groupBy };
    if (categories.length) params.categories = categories.join(",");
    // Absent, not empty: no year picked means "let the server choose its
    // default cohorts", which is not the same request as "no years".
    if (fiscalYears.length) params.fiscal_years = fiscalYears.join(",");
    getReviewFunnel(params)
      .then((data) => {
        setFunnel(data);
        setError("");
      })
      .catch(() => setError("Could not load the review funnel."));
  }, [manager, asOf, groupBy, categories, fiscalYears]);

  useEffect(() => {
    load();
  }, [load]);

  // Every group key present in any cohort -- a group can exist in last year
  // and not this one, and dropping it would hide exactly the comparison the
  // page is for.
  const groupKeys = useMemo(() => {
    if (!funnel) return [];
    const seen = new Set();
    funnel.cohorts.forEach((c) =>
      (c.groups || []).forEach((g) => seen.add(g.key))
    );
    return [...seen];
  }, [funnel]);

  // Selectable years, newest first: whatever the response covers, whatever
  // is currently picked, and this calendar year, extended a few years below
  // the earliest of those. Including the current picks matters -- picking a
  // year narrows the response to it, and an option derived only from the
  // response would drop the neighbours the manager was about to compare
  // against, or itself when a picked year turns out to hold no work.
  const yearOptions = useMemo(() => {
    const seen = [
      ...(funnel ? funnel.cohorts.map((c) => c.fiscal_year) : []),
      ...fiscalYears,
      new Date().getFullYear(),
    ].filter((y) => Number.isFinite(y));
    const years = [];
    for (let y = Math.max(...seen); y >= Math.min(...seen) - YEARS_BELOW; y -= 1) {
      years.push(y);
    }
    return years;
  }, [funnel, fiscalYears]);

  if (!manager) {
    return <div style={{ padding: 24 }}>Review is managers only.</div>;
  }

  const toggleYear = (year) =>
    setFiscalYears((prev) =>
      prev.includes(year)
        ? prev.filter((y) => y !== year)
        : [...prev, year].sort((a, b) => b - a)
    );

  const toggleCategory = (key) =>
    setCategories((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>Review</h2>
      <p style={{ color: "#6B7280", marginTop: -8 }}>
        Where the season sits against previous years. Each year is measured on
        the same month and day of its own year.
      </p>
      {/* Assignment history has never been recorded anywhere in this app,
          so the backend has nothing but today's assignment rows to read.
          Said out loud here, because a per-employee count silently read as
          historical is the kind of number people make decisions on. */}
      <p style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>
        The employee cut reflects current assignments — work from earlier
        years is counted against whoever holds the file today, not whoever
        did it at the time.
      </p>

      <div
        style={{
          display: "flex", gap: 16, alignItems: "center",
          flexWrap: "wrap", margin: "16px 0",
        }}
      >
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          As of
          <input
            type="date"
            data-testid="as-of-input"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </label>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          Cut by
          <select
            data-testid="cut-select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="category">Category</option>
            <option value="employee">Employee</option>
          </select>
        </label>

        <div style={{ display: "flex", gap: 4 }} role="group" aria-label="Categories">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => toggleCategory(c.key)}
              aria-pressed={categories.includes(c.key)}
              style={chipStyle(categories.includes(c.key))}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fiscal-year cohorts. Picking none leaves the server on its own
          default -- the three most recent years that have work -- so the
          only reason to touch this is to reach a year outside that. */}
      <div
        style={{ display: "flex", gap: 4, flexWrap: "wrap", margin: "0 0 16px" }}
        role="group"
        aria-label="Fiscal years"
      >
        <span style={{ color: "#6B7280", fontSize: 13, alignSelf: "center", marginRight: 4 }}>
          Fiscal years
        </span>
        {yearOptions.map((y) => (
          <button
            key={y}
            data-testid={`year-chip-${y}`}
            onClick={() => toggleYear(y)}
            aria-pressed={fiscalYears.includes(y)}
            style={chipStyle(fiscalYears.includes(y))}
          >
            {y}
          </button>
        ))}
      </div>

      {error ? <p style={{ color: "#B91C1C" }}>{error}</p> : null}

      {funnel === null ? (
        <p style={{ color: "#6B7280" }}>Loading…</p>
      ) : funnel.cohorts.length === 0 ? (
        <p style={{ color: "#6B7280" }}>No tasks to compare yet.</p>
      ) : (
        <>
          {funnel.cohorts.map((c) => (
            <CoverageNotice key={c.fiscal_year} cohort={c} />
          ))}
          {groupKeys.map((key) => (
            <section key={key} style={{ marginTop: 28 }}>
              <FunnelChart funnel={funnel} groupKey={key} />
            </section>
          ))}
        </>
      )}
    </div>
  );
}
